import React, { useState } from 'react';
import { ContactInfo, getContactLinkData } from 'utils';
import { Container, IconButton, Stack, styled, Typography } from '@mui/material';
import { ReactComponent as AccountInactiveSVG } from 'assets/images/DoneIneligible/AccountInactive.svg';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { SignUpHelpDialog } from 'components/SignUpHelpDialog/SignUpHelpDialog';
import { Button } from 'components/Button/Button';
import { ContentCopy, Email, Phone } from '@mui/icons-material';
import { ContactLink } from 'features/auth/types';
import { Alert } from 'components/Alert/Alert';

const LinkButton = styled(Button)(({ theme }) => ({
  display: 'inline',
  padding: 0,
  font: 'inherit',
  cursor: 'pointer',
  verticalAlign: 'baseline',
}));

const NoContactInfoContent = ({ handleOpenHelp }: { handleOpenHelp: () => void }) => {
  const { translation } = useTranslationContext();

  return (
    <Typography
      id="done-ineligible__no-contact-info-body-text"
      whiteSpace="pre-wrap"
      textAlign="center"
      variant="callout"
      color="gray.800"
    >
      {translation('done_or_ineligible_no_contact_info_line_one')}
      <br />
      <br />
      {translation('done_or_ineligible_no_contact_info_line_two')}
      <br />
      <br />
      {translation('done_or_ineligible_no_contact_info_line_three')}
      <LinkButton id="done-ineligible__no-contact-info-help-link" onClick={handleOpenHelp}>
        Wellframe
      </LinkButton>
    </Typography>
  );
};

const CTAButton = styled(Button)(({ theme }) => ({
  gap: theme.spacing(2),
  paddingInline: theme.spacing(8),
}));

const EmailContent = ({
  emailInfo,
  copyToClipboard,
}: {
  emailInfo: ContactInfo;
  copyToClipboard: (str: string) => void;
}) => {
  const { translation } = useTranslationContext();

  return (
    <>
      <Typography
        id="done-ineligible__email-body-text"
        whiteSpace="pre-wrap"
        textAlign="center"
        variant="callout"
        color="gray.800"
      >
        {translation('done_or_ineligible_email_and_phone_body').replaceAll('\\n', '\n')}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={1}>
        <IconButton
          id="done-ineligible__copy-email-button"
          aria-label={translation('copy_to_clipboard')}
          onClick={() => copyToClipboard(emailInfo.displayValue)}
          sx={{ color: 'common.black' }}
        >
          <ContentCopy />
        </IconButton>
        <Typography
          id="done-ineligible__email-display"
          variant="title3"
          color="gray.800"
          fontWeight="bold"
        >
          {emailInfo.displayValue}
        </Typography>
      </Stack>
      <CTAButton
        id="done-ineligible__email-cta"
        size="xlarge"
        variant="primary"
        href={emailInfo.url}
      >
        <Email />
        {translation('done_or_ineligible_email_cta')}
      </CTAButton>
    </>
  );
};

const PhoneContent = ({ phoneInfo }: { phoneInfo: ContactInfo }) => {
  const { translation } = useTranslationContext();

  return (
    <>
      <Typography
        id="done-ineligible__phone-body-text"
        whiteSpace="pre-wrap"
        textAlign="center"
        variant="callout"
        color="gray.800"
      >
        {translation('done_or_ineligible_email_and_phone_body').replaceAll('\\n', '\n')}
      </Typography>
      <Typography
        id="done-ineligible__phone-display"
        textAlign="center"
        variant="title3"
        color="gray.800"
        fontWeight="bold"
      >
        {phoneInfo.displayValue}
      </Typography>
      <CTAButton
        id="done-ineligible__phone-cta"
        size="xlarge"
        variant="primary"
        href={phoneInfo.url}
      >
        <Phone />
        {translation('done_or_ineligible_phone_cta')}
      </CTAButton>
    </>
  );
};

export const DoneIneligibleState = ({ contactLink }: { contactLink: ContactLink }) => {
  const { translation } = useTranslationContext();
  const [isHelpDialogOpen, setIsHelpDialogOpen] = useState(false);
  const [isShowingCopyToast, setIsShowingCopyToast] = useState(false);

  const copyToClipboard = (str: string) => {
    navigator.clipboard.writeText(str);
    setIsShowingCopyToast(true);
  };

  const contactLinkData = getContactLinkData(contactLink);
  let content: React.ReactNode;
  if (contactLink?.type === 'email' && contactLinkData) {
    content = <EmailContent emailInfo={contactLinkData} copyToClipboard={copyToClipboard} />;
  } else if (contactLink?.type === 'phone' && contactLinkData) {
    content = <PhoneContent phoneInfo={contactLinkData} />;
  } else {
    content = <NoContactInfoContent handleOpenHelp={() => setIsHelpDialogOpen(true)} />;
  }

  return (
    <Container maxWidth="sm" sx={{ display: 'flex', alignItems: 'center', minHeight: '100vh' }}>
      <Stack alignItems="center" spacing={5}>
        <AccountInactiveSVG />
        <Typography
          variant="title3"
          color="primary"
          textAlign="center"
          id="done-ineligible-dialog-title"
        >
          {translation('done_or_ineligible_title')}
        </Typography>
        {content}
      </Stack>
      <SignUpHelpDialog
        isHelpDialogOpen={isHelpDialogOpen}
        onHelpDialogClose={() => setIsHelpDialogOpen(false)}
        signUpFormData={{
          accessCode: '',
          memberId: '',
          memberFirstName: '',
          memberLastName: '',
          memberDateOfBirth: '',
          memberStateOfResidence: '',
          memberGender: '',
          memberPhoneNumber: '',
          memberEmail: '',
          memberConfirmedEmail: '',
          password: '',
          confirmedPassword: '',
          smsOptIn: false,
          privacyPolicyAccepted: false,
          eulaPolicyAccepted: false,
        }}
      />
      <Alert
        isPresented={isShowingCopyToast}
        sx={{ marginTop: 4 }}
        message={translation('copied_to_clipboard')}
        handleDismiss={() => setIsShowingCopyToast(false)}
      />
    </Container>
  );
};
